// ---------------------------------------------------------------------------
// Homefront v3 — maker logic (custom homefront setup)
// ---------------------------------------------------------------------------
//
// Pure helpers behind the HomefrontMakerV3 screen. Resources are out of the
// prototype, so authoring a Grid is placing buildings: pick a footprint shape,
// then tap a hex. The shapes are the Grid's own lattice shapes — one cell, or
// three cells along one of the hex's three axes, or a three-cell bend:
//
//   single   the tapped hex                                          ⬢
//   column   3 hexes stacked up the column, centred on the tap       │
//   rise     3 hexes along the rising axis (lower-left → upper-right) ╱
//   fall     3 hexes along the falling axis (upper-left → lower-right) ╲
//   vee      the tap and its two upper neighbours — a 120° bend      ⋀
//
// The tapped hex is ALWAYS the shape's centre cell (the middle entry of the
// footprint). Footprints cross corridor seams freely — the field is one
// continuous 21×15 lattice — but a shape whose cell would fall off the field
// edge cannot be placed there (no shifting/clamping). A structure places on
// any empty hex (yield comes from lots, not structures: see subplotEconomy).
//
// All mutators return a NEW homefront (or the same reference for no-ops) so
// the screen can hang them straight off React state.

import { rules } from './config.js'
import {
    SUBPLOT_COUNT,
    makeHomefront,
    cloneHomefront,
    placeStructure,
} from './subplotGrid.js'
import { HEX_DIRS, toAxial, cellsAtOffsets } from './hexGrid.js'
export const STRUCTURE_SHAPES = [
    { key: 'single', label: 'Single', cells: 1 },
    { key: 'column', label: 'Tall',   cells: 3 },
    { key: 'rise',   label: 'Rise',   cells: 3 },
    { key: 'fall',   label: 'Fall',   cells: 3 },
    { key: 'vee',    label: 'Vee',    cells: 3 },
]

// A fresh all-empty (undeveloped) field for the maker to start from.
export function emptyMakerHomefront() {
    return makeHomefront(() => null)
}

// Each shape's cells as AXIAL offsets [dq, dr] from the tapped hex (see
// hexGrid.HEX_DIRS: 0 = down-right, 1 = up-right, 2 = up, 3 = up-left,
// 4 = down-left, 5 = down). The tap is always the middle entry so
// footprintCenter (badge host) lands on it.
const D = HEX_DIRS
export const SHAPE_OFFSETS = {
    single: [[0, 0]],
    column: [D[2], [0, 0], D[5]],   // up, tap, down
    rise:   [D[4], [0, 0], D[1]],   // down-left, tap, up-right
    fall:   [D[3], [0, 0], D[0]],   // up-left, tap, down-right
    vee:    [D[3], [0, 0], D[1]],   // up-left, tap, up-right
}

// The footprint the given shape would occupy if placed via a tap on hex s —
// centred on s, crossing corridor seams freely. Returns null for an unknown
// shape key or when any cell would fall off the field edge (the shape is not
// shifted to fit; that spot just can't host it).
export function shapeFootprint(shapeKey, s) {
    const offsets = SHAPE_OFFSETS[shapeKey]
    if (!offsets) return null
    const cells = cellsAtOffsets(s, offsets)
    return cells.length === offsets.length ? cells : null
}

export function canPlaceFootprint(homefront, footprint) {
    if (!footprint) return false
    return footprint.every(s => homefront.subplots[s]?.structureId == null)
}

// (Roads and the Port are retired; a persisted grid's stale `port` field is
// dropped on entry to the maker.)

export function structureCount(homefront) {
    return Object.keys(homefront.structures).length
}

// Infer a structure's shape key from its footprint geometry. Structures don't
// persist their shape (presets/autogen build footprints directly), so we read
// it back off the cells: 1 cell = single; 3 cells classify by the axial
// offsets of the two ends from the middle cell. Returns null for a footprint
// no maker shape produces (a legacy square-grid preset's, say).
export function structureShape(struct) {
    const fp = struct?.footprint
    if (!fp) return null
    if (fp.length === 1) return 'single'
    if (fp.length !== 3) return null
    const ax = fp.map(toAxial)
    // The centre is whichever cell the other two are both adjacent to.
    for (let c = 0; c < 3; c++) {
        const ends = [0, 1, 2].filter(i => i !== c).map(i => [ax[i].q - ax[c].q, ax[i].r - ax[c].r])
        const key = ends.map(([dq, dr]) => `${dq},${dr}`).sort().join('|')
        for (const [shape, offsets] of Object.entries(SHAPE_OFFSETS)) {
            if (offsets.length !== 3) continue
            const want = [offsets[0], offsets[2]].map(([dq, dr]) => `${dq},${dr}`).sort().join('|')
            if (key === want) return shape
        }
    }
    return null
}

// How many buildings of each shape are on the board: { single: 2, tall: 1, … }.
export function shapeCounts(homefront) {
    const counts = {}
    for (const struct of Object.values(homefront.structures)) {
        const shape = structureShape(struct)
        if (shape) counts[shape] = (counts[shape] ?? 0) + 1
    }
    return counts
}

// Maker-built structures get sequential ids (U-1, U-2, …) that never collide
// with whatever is already on the board (including preset ids after an
// auto-generate, which use their own prefixes).
function nextStructureId(homefront) {
    let max = 0
    for (const id of Object.keys(homefront.structures)) {
        const m = /^U-(\d+)$/.exec(id)
        if (m) max = Math.max(max, Number(m[1]))
    }
    return `U-${max + 1}`
}

// Place the selected shape via a tap on hex s. Returns the new homefront,
// or the SAME homefront if the placement is blocked (overlap / at the total
// cap / at the per-shape cap).
export function addStructureAt(homefront, shapeKey, s) {
    if (structureCount(homefront) >= rules().homefrontMaxStructures) return homefront
    if ((shapeCounts(homefront)[shapeKey] ?? 0) >= rules().homefrontMaxPerShape) return homefront
    const footprint = shapeFootprint(shapeKey, s)
    if (!canPlaceFootprint(homefront, footprint)) return homefront
    const next = cloneHomefront(homefront)
    placeStructure(next, nextStructureId(homefront), footprint)
    return next
}

// Remove a whole building (any of its cells was tapped). No-op on unknown id.
export function removeStructureById(homefront, id) {
    const struct = homefront.structures[id]
    if (!struct) return homefront
    const next = cloneHomefront(homefront)
    for (const s of struct.footprint) next.subplots[s].structureId = null
    delete next.structures[id]
    return next
}

// A homefront a player may lock in: right shape, building count in range,
// no shape over its per-shape cap. (The Port is assigned at lock-in, so it's
// no part of validity here.)
export function validateMaker(homefront) {
    const count = homefront ? structureCount(homefront) : 0
    const wellFormed = !!homefront && Array.isArray(homefront.subplots)
        && homefront.subplots.length === SUBPLOT_COUNT
    const shapesOk = wellFormed
        && Object.values(shapeCounts(homefront)).every(n => n <= rules().homefrontMaxPerShape)
    return {
        valid: wellFormed && shapesOk
            && count >= rules().homefrontMinStructures && count <= rules().homefrontMaxStructures,
        count,
        min: rules().homefrontMinStructures,
        max: rules().homefrontMaxStructures,
        maxPerShape: rules().homefrontMaxPerShape,
    }
}

// Is a value (e.g. a stored/persisted homefront) usable as a maker start state?
export function isV3Homefront(value) {
    return !!value
        && Array.isArray(value.subplots)
        && value.subplots.length === SUBPLOT_COUNT
        && typeof value.structures === 'object' && value.structures !== null
}
